import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { 
  FiClock, 
  FiCheckCircle, 
  FiShield, 
  FiMessageSquare, 
  FiFileText, 
  FiAlertTriangle, 
  FiUser, 
  FiEdit3,
  FiEye,
  FiArchive
} = FiIcons;

const getEventConfig = (action) => {
  const text = action.toLowerCase();
  if (text.includes('opprettet')) return { icon: FiFileText, color: 'blue' };
  if (text.includes('bekreftet')) return { icon: FiCheckCircle, color: 'green' };
  if (text.includes('kontradiksjon')) return { icon: FiShield, color: 'indigo' };
  if (text.includes('utredning') || text.includes('tildelt')) return { icon: FiShield, color: 'purple' };
  if (text.includes('status')) return { icon: FiEdit3, color: 'orange' };
  if (text.includes('melding') || text.includes('chat')) return { icon: FiMessageSquare, color: 'cyan' };
  if (text.includes('innsyn')) return { icon: FiEye, color: 'amber' };
  if (text.includes('arkivert') || text.includes('løst')) return { icon: FiArchive, color: 'emerald' };
  return { icon: FiClock, color: 'gray' };
};

const CaseAuditTimeline = ({ report }) => {
  const events = [
    {
      date: report.created_at || report.createdAt,
      action: 'Sak opprettet i systemet',
      user: 'System (Varsler)',
      isSystem: true
    },
    ...(report.history || [])
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-[32px] p-10 border border-gray-100 dark:border-gray-700 shadow-sm">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-xs font-black text-gray-400 uppercase tracking-[0.2em]">Sakslogg (Audit Trail)</h2>
        <div className="bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
          <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
            {events.length} Hendelser
          </span>
        </div>
      </div>

      <div className="space-y-8">
        {events.map((event, idx) => {
          const config = getEventConfig(event.action);
          return (
            <motion.div 
              key={idx} 
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="flex gap-6 group"
            >
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-xl bg-white dark:bg-gray-800 border-2 flex items-center justify-center shadow-sm transition-colors
                  ${config.color === 'blue' ? 'border-blue-500 text-blue-500 ring-4 ring-blue-50 dark:ring-blue-900/20' : ''}
                  ${config.color === 'green' ? 'border-green-500 text-green-500 ring-4 ring-green-50 dark:ring-green-900/20' : ''}
                  ${config.color === 'indigo' ? 'border-indigo-500 text-indigo-500 ring-4 ring-indigo-50 dark:ring-indigo-900/20' : ''}
                  ${config.color === 'purple' ? 'border-purple-500 text-purple-500 ring-4 ring-purple-50 dark:ring-purple-900/20' : ''}
                  ${config.color === 'orange' ? 'border-orange-500 text-orange-500 ring-4 ring-orange-50 dark:ring-orange-900/20' : ''}
                  ${config.color === 'cyan' ? 'border-cyan-500 text-cyan-500 ring-4 ring-cyan-50 dark:ring-cyan-900/20' : ''}
                  ${config.color === 'amber' ? 'border-amber-500 text-amber-500 ring-4 ring-amber-50 dark:ring-amber-900/20' : ''}
                  ${config.color === 'emerald' ? 'border-emerald-500 text-emerald-500 ring-4 ring-emerald-50 dark:ring-emerald-900/20' : ''}
                  ${config.color === 'gray' ? 'border-gray-400 text-gray-400 ring-4 ring-gray-50 dark:ring-gray-900/20' : ''}
                `}>
                  <SafeIcon icon={config.icon} size={18} />
                </div>
                {idx !== events.length - 1 && (
                  <div className="w-0.5 flex-1 bg-gray-100 dark:bg-gray-700 my-2"></div>
                )}
              </div>

              <div className="pb-8 flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-1">
                  <p className="text-sm font-bold text-gray-800 dark:text-gray-200">{event.action}</p>
                  <p className="text-[11px] text-gray-400 font-mono">{format(new Date(event.date), 'dd.MM.yyyy HH:mm', { locale: nb })}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Ansvarlig: {event.user}</span>
                  {event.isSystem && (
                    <span className="text-[9px] font-black bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded uppercase tracking-wider">
                      System
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-6 pt-6 border-t border-gray-50 dark:border-gray-700">
        <p className="text-[10px] text-gray-400 font-medium text-center italic">
          Denne loggen er låst og kan ikke endres. Alle handlinger loggføres med tidsstempel og ansvarlig bruker for å sikre sporbarhet.
        </p>
      </div>
    </div>
  );
};

export default CaseAuditTimeline;